var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useEffect, useContext, } from 'react';
import { Box, Grid, Typography, DialogContent, Dialog, DialogTitle, } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
// Internal dependencies
import { axiosClient } from '../axios';
import { TextFieldSG, ButtonSG } from '../components';
import { ENDPOINTS, LOG_LEVELS } from '../constants';
import { UserContext, getAuthHeader } from '../auth';
import { appStyles } from '../App.styles';
import { workflowStyles } from './workflowStyles';
import { ACTION_TYPES } from './constants';
/** Custom Workflow Prompt */
export const CustomWorkflowPrompt = function (props) {
    const user = useContext(UserContext);
    const { t, i18n } = useTranslation();
    // Extract props
    const { prompt, updateWorkflowPromptsUpdatedFlag, customWorkflowPromptOpen, setCustomWorkflowPromptOpen, sx, } = props;
    // Validate schema
    const validationSchema = Yup.object({
        name: Yup.string().required('Required'),
        promptContent: Yup.string().required('Required'),
    });
    /** Initialize values, validation schema, submit action */
    const formik = useFormik({
        initialValues: Object.assign(Object.assign({}, prompt), { name: '' }),
        validationSchema: validationSchema,
        validateOnChange: false,
        onSubmit: (values) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            try {
                // Get body request
                const body = {
                    workflowAction: values,
                };
                // Get auth header
                const authHeader = yield getAuthHeader(user);
                // Get headers
                const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                let res = null;
                // If create a new prompt
                res = yield axiosClient.post(`${process.env.SERVER_URL}${ENDPOINTS.WORKFLOW}${ENDPOINTS.CREATE_WORKFLOW_ACTION}`, body, { headers });
                // Get updated prompts
                const workflowActions = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.workflowActions;
                updateWorkflowPromptsUpdatedFlag();
                // Close the custom form
                setCustomWorkflowPromptOpen(false);
                // Clear the form
                formik.setValues({
                    name: '',
                    promptContent: '',
                    type: ACTION_TYPES.PROMPT,
                });
            }
            catch (err) {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `CustomWorkflowPrompt, onSubmit, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
                // Check if the error is from axios
                if ((_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.status) {
                    // If yes, notify user with the server error message
                    toast.error((_e = (_d = err === null || err === void 0 ? void 0 : err.response) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.message);
                    return;
                }
                ;
                toast.error(t('Something went wrong. We are figuring it out.'));
            }
            ;
        }),
    });
    useEffect(() => {
        formik.setValues({
            name: '',
            promptContent: prompt === null || prompt === void 0 ? void 0 : prompt.promptContent,
            type: ACTION_TYPES.PROMPT,
        });
    }, [prompt]);
    /**
     *
     */
    const handleCancelClick = () => {
        setCustomWorkflowPromptOpen(false);
        formik.setValues({ name: '', promptContent: '', type: ACTION_TYPES.PROMPT });
    };
    return (_jsxs(Dialog, Object.assign({ sx: {
            '& .MuiDialog-paper': {
                backgroundColor: 'white.main',
                borderRadius: '1rem',
                maxWidth: '1024px',
                width: '100%',
                height: {
                    sm: '80%',
                    xs: '90%',
                },
            }
        }, open: customWorkflowPromptOpen, onClose: () => { setCustomWorkflowPromptOpen(false); } }, { children: [_jsx(DialogTitle, Object.assign({ sx: {
                    padding: '1.5rem 1.5rem 0rem',
                } }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text24), { color: 'black.main', padding: '0' }) }, { children: t('Save As Custom Prompt') })) })), _jsx(DialogContent, Object.assign({ sx: { padding: '1.5rem 1.5rem 0' } }, { children: _jsxs(Box, Object.assign({ component: "form", onSubmit: formik.handleSubmit, sx: {
                        margin: '1.5rem 0 0',
                    } }, { children: [_jsx(TextFieldSG
                        // Company text field
                        , { 
                            // Company text field
                            name: 'name', label: t('Set a prompt name'), placeholder: t('Blog Generator'), value: formik.values.name, onChange: formik.handleChange, error: formik.touched.name && Boolean(formik.errors.name), helperText: formik.touched.name && formik.errors.name }), _jsx(TextFieldSG
                        // Company text field
                        , { 
                            // Company text field
                            name: 'promptContent', label: t('Enter your prompt'), placeholder: t('Be specific and give steps. Use bracket for [placeholders].'), value: formik.values.promptContent, onChange: formik.handleChange, error: formik.touched.promptContent && Boolean(formik.errors.promptContent), helperText: formik.touched.promptContent && formik.errors.promptContent, multiline: true, maxRows: 12 }), _jsxs(Grid, Object.assign({ container: true, spacing: 1, sx: { justifyContent: 'flex-end' } }, { children: [_jsx(Grid, Object.assign({ item: true }, { children: _jsx(ButtonSG, Object.assign({ variant: "contained", type: "submit" }, { children: t('Save') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(ButtonSG, Object.assign({ variant: "text", onClick: handleCancelClick, sx: Object.assign({}, workflowStyles.cancelButton) }, { children: t('Cancel') })) }))] }))] })) }))] })));
};
